/**
 * Keyword Intelligence Cache Service
 *
 * Caches keyword research data for instant access when switching tabs.
 * Uses localStorage for persistence with TTL.
 */

export interface CachedKeywordData {
  keyword: string;
  country: string;
  device: 'desktop' | 'mobile';
  serpResults: CachedSerpResult[];
  paaQuestions: string[];
  relatedSearches: string[];
  competitorDomains: CachedCompetitor[];
  timestamp: number;
}

export interface CachedSerpResult {
  position: number;
  title: string;
  link: string;
  domain: string;
  snippet?: string;
}

export interface CachedCompetitor {
  domain: string;
  position: number;
  title: string;
  url: string;
}

const CACHE_KEY_PREFIX = 'unicorn_keyword_cache_';
const CACHE_TTL = 24 * 60 * 60 * 1000; // 24 hours
const MAX_CACHED_KEYWORDS = 50; // Limit to prevent localStorage bloat

// In-memory cache for faster access during session
const memoryCache = new Map<string, CachedKeywordData>();

/**
 * Generate a cache key from keyword params
 */
function getCacheKey(keyword: string, country: string, device: string): string {
  return `${keyword.toLowerCase().trim()}_${country}_${device}`;
}

/**
 * Extract domain from URL
 */
function extractDomain(url: string): string {
  try {
    const urlObj = new URL(url);
    return urlObj.hostname.replace('www.', '');
  } catch {
    return url;
  }
}

export const keywordCacheService = {
  /**
   * Get cached data for a keyword
   */
  get(keyword: string, country: string, device: string): CachedKeywordData | null {
    const key = getCacheKey(keyword, country, device);

    // Check memory cache first
    if (memoryCache.has(key)) {
      const cached = memoryCache.get(key)!;
      if (Date.now() - cached.timestamp < CACHE_TTL) {
        return cached;
      }
      memoryCache.delete(key);
    }

    // Check localStorage
    try {
      const stored = localStorage.getItem(CACHE_KEY_PREFIX + key);
      if (stored) {
        const cached: CachedKeywordData = JSON.parse(stored);
        if (Date.now() - cached.timestamp < CACHE_TTL) {
          memoryCache.set(key, cached);
          return cached;
        }
        // Expired, remove it
        localStorage.removeItem(CACHE_KEY_PREFIX + key);
      }
    } catch (e) {
      console.warn('Error reading keyword cache:', e);
    }

    return null;
  },

  /**
   * Cache keyword research data
   */
  set(
    keyword: string,
    country: string,
    device: 'desktop' | 'mobile',
    serpResults: any[],
    paaQuestions: string[],
    relatedSearches: string[]
  ): void {
    const key = getCacheKey(keyword, country, device);

    // Extract competitor domains from SERP results
    const competitorDomains: CachedCompetitor[] = serpResults
      .filter(r => r.link)
      .map(r => ({
        domain: extractDomain(r.link),
        position: r.position,
        title: r.title,
        url: r.link
      }));

    const cachedData: CachedKeywordData = {
      keyword,
      country,
      device,
      serpResults: serpResults.map(r => ({
        position: r.position,
        title: r.title,
        link: r.link,
        domain: extractDomain(r.link),
        snippet: r.snippet
      })),
      paaQuestions,
      relatedSearches,
      competitorDomains,
      timestamp: Date.now()
    };

    // Store in memory
    memoryCache.set(key, cachedData);

    // Store in localStorage
    try {
      // Clean up old entries if we have too many
      this.cleanup();
      localStorage.setItem(CACHE_KEY_PREFIX + key, JSON.stringify(cachedData));
    } catch (e) {
      console.warn('Error storing keyword cache:', e);
    }
  },

  /**
   * Update cache with additional data (for background loading)
   */
  update(
    keyword: string,
    country: string,
    device: string,
    updates: Partial<Omit<CachedKeywordData, 'keyword' | 'country' | 'device' | 'timestamp'>>
  ): void {
    const existing = this.get(keyword, country, device);
    if (existing) {
      const updated = {
        ...existing,
        ...updates,
        timestamp: Date.now()
      };
      const key = getCacheKey(keyword, country, device);
      memoryCache.set(key, updated);
      try {
        localStorage.setItem(CACHE_KEY_PREFIX + key, JSON.stringify(updated));
      } catch (e) {
        console.warn('Error updating keyword cache:', e);
      }
    }
  },

  /**
   * Clear cache for a specific keyword
   */
  clear(keyword: string, country: string, device: string): void {
    const key = getCacheKey(keyword, country, device);
    memoryCache.delete(key);
    try {
      localStorage.removeItem(CACHE_KEY_PREFIX + key);
    } catch (e) {
      console.warn('Error clearing keyword cache:', e);
    }
  },

  /**
   * Clear all keyword cache
   */
  clearAll(): void {
    memoryCache.clear();
    try {
      const keys = Object.keys(localStorage).filter(k => k.startsWith(CACHE_KEY_PREFIX));
      keys.forEach(k => localStorage.removeItem(k));
    } catch (e) {
      console.warn('Error clearing all keyword cache:', e);
    }
  },

  /**
   * Cleanup old entries to prevent localStorage bloat
   */
  cleanup(): void {
    try {
      const keys = Object.keys(localStorage).filter(k => k.startsWith(CACHE_KEY_PREFIX));

      if (keys.length <= MAX_CACHED_KEYWORDS) return;

      // Get all entries with timestamps
      const entries: { key: string; timestamp: number }[] = [];
      keys.forEach(k => {
        try {
          const data = JSON.parse(localStorage.getItem(k) || '{}');
          entries.push({ key: k, timestamp: data.timestamp || 0 });
        } catch {
          // Invalid entry, mark for removal
          entries.push({ key: k, timestamp: 0 });
        }
      });

      // Sort by timestamp, oldest first
      entries.sort((a, b) => a.timestamp - b.timestamp);

      // Remove oldest entries until we're under the limit
      const toRemove = entries.slice(0, entries.length - MAX_CACHED_KEYWORDS);
      toRemove.forEach(e => localStorage.removeItem(e.key));
    } catch (e) {
      console.warn('Error during cache cleanup:', e);
    }
  },

  /**
   * Get stats about the cache
   */
  getStats(): { count: number; oldestTimestamp: number | null } {
    try {
      const keys = Object.keys(localStorage).filter(k => k.startsWith(CACHE_KEY_PREFIX));
      let oldest: number | null = null;

      keys.forEach(k => {
        try {
          const data = JSON.parse(localStorage.getItem(k) || '{}');
          if (data.timestamp && (oldest === null || data.timestamp < oldest)) {
            oldest = data.timestamp;
          }
        } catch {}
      });

      return { count: keys.length, oldestTimestamp: oldest };
    } catch {
      return { count: 0, oldestTimestamp: null };
    }
  }
};
